/**
 * Central Bedrock content and context limits.
 * Single source of truth to avoid obscure magic numbers and ValidationException from quota.
 */

/**
 * Default max messages kept in the send window. Env-tunable per request via
 * `MERIDIAN_CONTEXT_WINDOW_MESSAGES` — see `resolveContextWindowConfig` in
 * `./context-window`. Raised from 24 (the old hardcoded window) to 60: prompt
 * caching makes the re-read history nearly free on the AWS path (cache reads are
 * ITPM-exempt) and DeepSeek auto-prefix-caches, so a cramped window only made
 * Meridian "forget" mid-conversation.
 */
export const DEFAULT_CONTEXT_WINDOW_MESSAGES = 60;

/**
 * Default max *estimated* input tokens of message history kept in the send window.
 * Env-tunable per request via `MERIDIAN_CONTEXT_WINDOW_MAX_TOKENS`.
 *
 * Why 100k and not the 200k model max: (a) tools + system prompt + output
 * reservation need headroom on a 200k model; (b) the Sonnet/Haiku tiers share
 * this code path; (c) token counts are chars-based ESTIMATES
 * (see `estimateMessagesTokens`) — the gap to the model max is the safety margin
 * for estimation error.
 */
export const DEFAULT_CONTEXT_WINDOW_MAX_TOKENS = 100_000;

/**
 * Default DeepSeek clamp for the token budget (DeepSeek context windows are
 * smaller than Claude's 200k). Env-tunable via
 * `MERIDIAN_CONTEXT_WINDOW_MAX_TOKENS_DEEPSEEK`.
 */
export const DEFAULT_CONTEXT_WINDOW_MAX_TOKENS_DEEPSEEK = 60_000;

/**
 * @deprecated Use `resolveContextWindowConfig().maxMessages` (env-tunable,
 * provider-aware) from `./context-window` instead. Kept as an alias to the new
 * default so any straggler import gets the new (larger) window, not the old 24.
 */
export const BEDROCK_CONTEXT_WINDOW_MESSAGES = DEFAULT_CONTEXT_WINDOW_MESSAGES;

/**
 * @deprecated The budget is now token-denominated — use
 * `resolveContextWindowConfig().maxTokens` from `./context-window`. This alias is
 * the char-equivalent of the new default token budget (~4 chars/token) so any
 * straggler import scales with the new window, not the old 96k.
 */
export const BEDROCK_CONTEXT_WINDOW_MAX_CHARS = DEFAULT_CONTEXT_WINDOW_MAX_TOKENS * 4;

/** Max characters per content block (text or serialized tool result). ~3k tokens at ~4 chars/token. */
export const MAX_CONTENT_BLOCK_CHARS = 12_000;

/**
 * Default max tool-call steps per streamText run. Env-tunable per request via
 * `MERIDIAN_STEP_COUNT_LIMIT` — see `resolveStepBudget` in `./step-budget`.
 *
 * Raised from 10 to 40. A step is one model request; 10 steps is a hard ceiling
 * on how much work a turn can do, and real requests routinely need more than
 * that (resolve a company → list its deals → read three of them → draft → send).
 * At 10 the loop stopped mid-task and the user had to type "continue", which
 * re-pays the whole prompt. Prompt caching makes the extra steps cheap (cache
 * reads are ITPM-exempt), so the binding constraint is WALL CLOCK, not steps —
 * which is why the step cap is now paired with a deadline stop condition
 * ({@link DEFAULT_TURN_DEADLINE_MS}) instead of standing alone.
 */
export const DEFAULT_STEP_COUNT_LIMIT = 40;

/**
 * @deprecated Use `resolveStepBudget().maxSteps` (env-tunable) from
 * `./step-budget`. Kept as an alias to the new default so any straggler import
 * gets the raised limit, not the old 10.
 */
export const BEDROCK_STEP_COUNT_LIMIT = DEFAULT_STEP_COUNT_LIMIT;

/**
 * Default wall-clock budget for one streaming turn, in milliseconds.
 * Env-tunable via `MERIDIAN_TURN_DEADLINE_MS`.
 *
 * Why a deadline at all: raising the step cap to 40 makes it possible for a turn
 * to outlive the serverless function (`maxDuration = 300` on the messages route).
 * A platform timeout is the WORST failure mode — the connection dies mid-stream,
 * `onFinish` never fires, and nothing is persisted. The deadline stops the loop
 * CLEANLY while there is still time to emit a final message and persist it.
 *
 * 270s leaves ~30s of headroom under the 300s function limit for the final model
 * response and the `onFinish` persistence writes.
 */
export const DEFAULT_TURN_DEADLINE_MS = 270_000;

/**
 * Steps allowed on the NON-streaming single-pass path (`generateBedrockResponse`).
 *
 * Lower than the streaming limit on purpose: its callers (quick-ask from the
 * Workspace add-on, one-shot summaries) answer inside a card request with a hard
 * wall-clock budget, and a question that genuinely needs ten lookups belongs in
 * the streaming chat — or behind a delegated agent, which spends its own steps
 * inside ONE step here. Enough for look-up-then-answer, not enough to run away.
 */
export const SINGLE_PASS_STEP_COUNT_LIMIT = 5;

/**
 * Ceiling a CALLER-supplied `maxSteps` is clamped to on the single-pass path.
 *
 * Its own constant, not the streaming cap. This clamp previously borrowed
 * {@link BEDROCK_STEP_COUNT_LIMIT}, which was fine while both were 10 — but the
 * streaming cap is a tuning knob that has since moved to 40, and the coupling
 * would have quietly raised this guard with it. The two paths have different
 * budgets (this one answers inside a card request), so they get different
 * numbers. 10 preserves the ceiling this clamp has always enforced.
 */
export const SINGLE_PASS_STEP_COUNT_MAX = 10;

/**
 * Max output tokens per streamText run. The provider default is the model max
 * (~64k), which needlessly bloats the request and adds output-TPM pressure for a
 * chat turn that rarely emits more than a few thousand tokens. 10k is generous
 * headroom for long answers + tool reasoning without the 64k reservation.
 */
export const BEDROCK_MAX_OUTPUT_TOKENS = 10_000;

/**
 * Retries for a failed Bedrock request. The AI SDK default (2) re-fires within
 * seconds and IGNORES the 429 `retry-after` header, so a rate-limited turn burns
 * the org's tiny req/min quota and amplifies one 429 into several. Cap at 1: a
 * single transient blip is retried, but a real rate-limit surfaces fast and the
 * user re-sends instead of us self-DoSing the limit.
 */
export const BEDROCK_MAX_RETRIES = 1;

/** Suffix appended when content is truncated for context limit. */
export const TRUNCATION_SUFFIX = '\n[... truncated for context limit]';
